// services/api.js (updated with detailed logging)
import axios from "axios";
import i18n from "../i18n/config";

// Base API configuration

// const API_BASE_URL = "https://api.theuniflow.com/";
// Defaults to the local backend. When REACT_APP_API_BASE_URL is set to an empty
// string (e.g. for localtunnel testing), calls become same-origin relative paths
// that CRA's dev proxy forwards to the backend.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "https://api.theuniflow.com";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Store notification function reference
let notificationHandler = null;

// Function to set notification handler
export const setNotificationHandler = (handler) => {
  notificationHandler = handler;
};

// Helper function to translate error codes
const translateErrorCode = (errorCode) => {
  if (!errorCode) return i18n.t("errors:GENERIC_ERROR");

  // Try to translate the error code
  const translated = i18n.t(`errors:${errorCode}`, { defaultValue: null });

  // If translation not found, return the generic error
  return translated || i18n.t("errors:GENERIC_ERROR");
};

// Helper function to format and log request details
const logRequest = (config) => {
  console.group(`🚀 Request: ${config.method?.toUpperCase()} ${config.url}`);

  console.log("Headers:", config.headers);

  if (config.params) {
    console.log("Params:", config.params);
  }

  if (config.data) {
    console.log("Body:", config.data);
  }

  console.groupEnd();
};

// Helper function to format and log response details
const logResponse = (response) => {
  console.group(
    `✅ Response: ${response.config.method?.toUpperCase()} ${
      response.config.url
    }`,
  );

  console.log("Status:", response.status);
  console.log("Headers:", response.headers);
  console.log("Data:", response.data);

  console.groupEnd();
};

// Helper function to format and log error details
const logError = (error) => {
  const method = error.config?.method?.toUpperCase() || "UNKNOWN";
  const url = error.config?.url || "UNKNOWN";

  console.group(`❌ Error: ${method} ${url}`);

  if (error.response) {
    console.log("Status:", error.response.status);
    console.log("Headers:", error.response.headers);
    console.log("Data:", error.response.data);
  } else if (error.request) {
    console.log("Request made but no response received");
    console.log("Request:", error.request);
  } else {
    console.log("Error Message:", error.message);
  }

  console.groupEnd();
};

// Request interceptor to add auth token and log requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language = i18n.language || window.localStorage.getItem("i18nextLng") || "en";
    config.headers["Accept-Language"] = language;

    // If we're sending FormData, let the browser set the correct multipart boundary
    if (config.data instanceof FormData) {
      // axios merges headers; ensure we don't force JSON for multipart requests
      if (config.headers && config.headers["Content-Type"]) {
        delete config.headers["Content-Type"];
      }
    }

    // Log request details
    logRequest(config);

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor to handle common errors and show notifications
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    logResponse(response);

    return response;
  },
  (error) => {
    // Log error responses
    logError(error);

    let errorMessage = translateErrorCode("GENERIC_ERROR");

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      if (status === 401) {
        // Token expired or invalid
        errorMessage = translateErrorCode("ERROR_SESSION_EXPIRED");
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiresAt");
        localStorage.removeItem("user");
        // In development, do not hard-redirect so we can see errors

        return Promise.reject(error); // Don't show notification for auth errors
      } else if (status === 403) {
        // Permission denied
        errorMessage = translateErrorCode("ERROR_PERMISSION_DENIED");
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiresAt");
        localStorage.removeItem("user");
        if (process.env.NODE_ENV === "production") {
          window.location.href = "/uni-admin/login";
        }
        return Promise.reject(error); // Don't show notification for permission errors
      } else if (status === 404) {
        errorMessage = translateErrorCode("ERROR_NOT_FOUND");
      } else if (status === 422) {
        errorMessage = translateErrorCode(
          data?.errorCode || "ERROR_VALIDATION_FAILED",
        );
      } else if (status === 500) {
        errorMessage = translateErrorCode(
          data?.errorCode || "ERROR_DATABASE_ERROR",
        );
      } else if (data?.errorCode) {
        errorMessage = translateErrorCode(data.errorCode);
      }
    } else if (error.request) {
      // Network error - don't show notification for server status checks
      if (error.config?.url?.includes("/api/app-config/server-status")) {
        return Promise.reject(error);
      }
      errorMessage = translateErrorCode("NETWORK_ERROR");
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Show error notification if handler is available
    // This will automatically redirect to network error page if message contains "Network Error"
    if (notificationHandler) {
      notificationHandler.showError(errorMessage);
    }

    return Promise.reject(error);
  },
);

export default api;

// ---------------------------------------------
// Image upload helper
// ---------------------------------------------

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
const MIN_DIMENSION = 50; // px
const MAX_DIMENSION = 4096; // px

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      URL.revokeObjectURL(objectUrl);
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read image dimensions"));
    };
    img.src = objectUrl;
  });
}

async function validateImageFile(file) {
  if (!file) {
    throw new Error("No file provided");
  }

  // Type check
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Invalid file type. Allowed: jpg, jpeg, png, gif, webp");
  }

  // Size check
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("File too large. Max size is 5MB");
  }

  // Dimension check
  const { width, height } = await readImageDimensions(file);
  if (
    width < MIN_DIMENSION ||
    height < MIN_DIMENSION ||
    width > MAX_DIMENSION ||
    height > MAX_DIMENSION
  ) {
    throw new Error(
      "Invalid image dimensions. Allowed: 50–4096 px for width and height",
    );
  }
}

/**
 * Upload a single image file to the server using multipart/form-data.
 * Field name MUST be `file` per backend contract.
 * @param {File} file
 * @returns {Promise}
 */
export async function uploadPhoto(file) {
  await validateImageFile(file);

  // Ensure filename has a valid extension; browsers typically provide this
  const filename =
    file.name && /\.[a-zA-Z0-9]+$/.test(file.name)
      ? file.name
      : `upload.${(file.type.split("/")[1] || "jpg").toLowerCase()}`;

  const formData = new FormData();
  formData.append("file", file, filename);

  // Do NOT set Content-Type manually; browser will set proper boundary
  return api.post("/api/photos/upload", formData);
}
